import { store } from '@/store/store'
import { nullifyData as chatUxNullify } from '@/chat-common/store/chat-ux.slice'
import { nullifyData as checkoutNullify } from '@/checkout/store/slice'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { AgentV2ReferencesActions } from '@/agent-v2/references/store/slice'
import { MatterArtifactSelectionActions } from '@/agent-v2/artifact-selection/store/slice'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ArtifactTemplatesActions } from '@/artifact-templates/store/slice'
import { DraftingActions } from '@/drafting/store/slice'
import { DraftWorkspaceActions } from '@/drafting/workspace/store/slice'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { MattersActions } from '@/matter/store/slice'
import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import * as Sentry from '@sentry/browser'
import { ConversationNavigationActions } from '@/chat-common/conversation-navigation/store/slice'
import { AccountActions } from '@/account/store/slice'
import { MedChronEventsActions } from '@/medical-chronology/events/store/slice'
import { MedChronReferencesActions } from '@/medical-chronology/references/store/slice'
import { MedChronAssetActions } from '@/medical-chronology/asset/store/slice'
import { TimelineEventsActions } from '@/timeline/events/store/slice'
import { TimelineReferencesActions } from '@/timeline/references/store/slice'
import { TimelineAssetActions } from '@/timeline/asset/store/slice'
import { ResponseFeedbackActions } from '@/response-feedback/store/slice'
import { MedSummaryEntriesActions } from '@/medical-billing-summaries/entries/store/entries.slice'
import { MedSummaryEntryEditActions } from '@/medical-billing-summaries/entries/store/entries-edit.slice'
import { MedSummaryReferencesActions } from '@/medical-billing-summaries/references/store/slice'
import { MedSummaryAssetActions } from '@/medical-billing-summaries/asset/store/slice'
import { ExternalStorageActions } from '@/user_files/external-storage/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { AgentV2TransportActions } from '@/agent-v2/transport/store/slice'
import { OcrQuotaUiActions } from '@/ocr_quota/store/slice'

/**
 * Clear State Data
 * Handles clearing of various slices of global state
 */
export async function clearReduxStateData() {
  // Chat UX
  store.dispatch(chatUxNullify())

  // Checkout
  store.dispatch(checkoutNullify())

  // Matters
  store.dispatch(MattersActions.nullifyData())

  // Matter Activity Feed
  store.dispatch(MatterActivityFeedActions.nullifyData())
  // Conversation Navigation (last viewed conversations)
  store.dispatch(ConversationNavigationActions.nullifyData())

  // Account (includes org directory users)
  store.dispatch(AccountActions.nullifyData())

  // External Storage
  store.dispatch(ExternalStorageActions.nullifyData())

  // OCR quota session acknowledgement
  store.dispatch(OcrQuotaUiActions.clearAcknowledgements())

  // User Files — the cached folder hierarchy, the file records under it, and the per-matter drive
  // sort all belong to the user who was signed in. Matters are shareable, so without this the next
  // user to sign in on this browser opens a shared matter holding the previous user's cache.
  store.dispatch(UserFoldersActions.reset())
  store.dispatch(UserFilesActions.reset())

  // Medical Billing Summaries
  store.dispatch(MedSummaryAssetActions.nullifyData())
  store.dispatch(MedSummaryEntriesActions.nullifyData())
  store.dispatch(MedSummaryEntryEditActions.nullifyData())
  store.dispatch(MedSummaryReferencesActions.nullifyData())

  // Medical Chronology
  store.dispatch(MedChronEventsActions.nullifyData())
  store.dispatch(MedChronReferencesActions.nullifyData())
  store.dispatch(MedChronAssetActions.nullifyData())

  // Timeline (living asset — events/references/asset slices; no job slice)
  store.dispatch(TimelineEventsActions.nullifyData())
  store.dispatch(TimelineReferencesActions.nullifyData())
  store.dispatch(TimelineAssetActions.nullifyData())

  // Response Feedback
  store.dispatch(ResponseFeedbackActions.nullifyData())

  // Conversations
  // Invalidate imperative socket attempts before the serializable conversation buckets reset.
  store.dispatch(AgentV2TransportActions.closeAllTransports())
  store.dispatch(AgentV2ConversationsActions.nullifyData())
  store.dispatch(AgentV2ReferencesActions.nullifyData())
  store.dispatch(MatterArtifactSelectionActions.nullifyData())

  // Matter Artifacts
  store.dispatch(MatterArtifactsActions.clearMatterArtifacts())

  // Content Viewer
  store.dispatch(ContentViewerActions.clearContentViewer())

  // Chat and draft state
  try {
    store.dispatch(ArtifactTemplatesActions.nullifyData())
    store.dispatch(DraftingActions.nullifyData())
    store.dispatch(DraftWorkspaceActions.nullifyData())
    store.dispatch(AgentConversationFormActions.nullifyData())
  } catch (error) {
    console.warn('Error clearing agent state data:', error)
    Sentry.captureException(error, {
      extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
    })
  }
}
